// src/components/organisation/OrganisationManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Building, Users, Briefcase, Calendar, Newspaper, Phone, X, ClipboardList, MapPin,
  Save, Eye, RefreshCw, AlertCircle, CheckCircle, Upload, FileText, Archive
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { organisationService } from '../../services/organisationService';
import { firestoreService } from '../../services/firestoreService';
import { TabNavigation } from '../settings/shared/TabNavigation';

// Import AdaptableFormFields components
import { 
  HeroSectionForm,
  LeadershipSectionForm,
  ServicesSectionForm,
  EventsSectionForm,
  NewsSectionForm,
  ContactSectionForm
} from '../manager/shared/AdaptableFormFields';
import { PostsSectionForm } from '../manager/shared/PostsSectionForm';
import { AboutSectionForm } from '../manager/shared/AdaptableFormFields';

// Management components for enabled management features
import { TeamFormFields } from '../manager/shared/TeamFormFields';
import { AssetFormFields } from '../manager/shared/AssetFormFields';
import { TaskFormFields} from '../manager/shared/TaskFormFields';
import { SiteFormFields } from '../manager/shared/SiteFormFields';
import { ClientFormFields } from '../manager/shared/ClientFormFields';
import { DocumentFormFields } from '../manager/shared/DocumentFormFields';
import { ResourcesFormFields } from '../manager/shared/ResourcesFormFields';
import { RecordsFormFields } from '../manager/shared/RecordsFormFields';

import { BookingSystemForm } from '../manager/booking/BookingSystemForm';

export const OrganisationManager = ({ bookId, book, onUpdate, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('about');
  const [formData, setFormData] = useState({});
  const [managementData, setManagementData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Get enabled management features from book settings
  const managementFeatures = book?.managementFeatures || {};
  const enabledManagementFeatures = Object.keys(managementFeatures).filter(
    key => managementFeatures[key]?.enabled
  );

  // Core organization sections
  const coreTabs = [
    { id: 'hero', label: 'Hero', icon: Eye, component: HeroSectionForm },     
    { id: 'about', label: 'About', icon: Building, component: AboutSectionForm },
    { id: 'leadership', label: 'Leadership', icon: Users, component: LeadershipSectionForm },
    { id: 'services', label: 'Services', icon: Briefcase, component: ServicesSectionForm },
    { id: 'events', label: 'Events', icon: Calendar, component: EventsSectionForm },
    { id: 'posts', label: 'Posts', icon: Newspaper, component: PostsSectionForm },
    { id: 'news', label: 'News', icon: Newspaper, component: NewsSectionForm },
    { id: 'contact', label: 'Contact', icon: Phone, component: ContactSectionForm },
    ...(book?.enableBooking ? [{
      id: 'booking',
      label: 'Booking System',
      icon: ClipboardList,
      component: BookingSystemForm
    }] : [])
  ];

  const getManagementFeatureLabel = (feature) => {
    const labels = {
      team: 'Team Management',
      assets: 'Asset Management', 
      tasks: 'Task Management',
      sites: 'Site Management',
      clients: 'Client Management',
      documents: 'Document Management',
      resources: 'Resource Management',
      records: 'Records Management'
    };
    return labels[feature] || feature;
  };

  const getManagementFeatureIcon = (feature) => {
    const icons = {
      team: Users,
      assets: Building,
      tasks: CheckCircle,
      sites: MapPin,
      clients: Briefcase,
      documents: FileText,
      resources: ClipboardList,
      records: Archive
    };
    return icons[feature] || ClipboardList;
  };

  const getManagementFeatureComponent = (feature) => {
    const components = {
      team: TeamFormFields,
      assets: AssetFormFields,
      tasks: TaskFormFields,
      sites: SiteFormFields,
      clients: ClientFormFields,
      documents: DocumentFormFields,
      resources: ResourcesFormFields,
      records: RecordsFormFields
    };
    return components[feature];
  };

  // Management feature tabs (dynamically added based on enabled features)
  const managementTabs = enabledManagementFeatures.map(feature => ({
    id: `mgmt-${feature}`,
    label: getManagementFeatureLabel(feature),
    icon: getManagementFeatureIcon(feature),
    component: getManagementFeatureComponent(feature),
    isManagement: true
  }));

  const allTabs = [...coreTabs, ...managementTabs];

  useEffect(() => {
    loadOrganisationData();
  }, [bookId]);

  const loadOrganisationData = async () => {
    try {
      setLoading(true);
      
      // Load all organisation sections
      const sections = await organisationService.getOrganisationSections(bookId);
      
      // Convert sections array to object keyed by section type
      const sectionsData = {};
      sections.forEach(section => {
        if (section.type) {
          sectionsData[section.type] = section;
        }
      });

      // Load management data if there are enabled management features
      let mgmtData = {};
      if (enabledManagementFeatures.length > 0) {
        for (const feature of enabledManagementFeatures) {
          try {
            const featureData = await organisationService.getManagementFeatureData(bookId, feature);
            if (featureData) {
              mgmtData[feature] = featureData;
            }
          } catch (error) {
            console.warn(`Could not load ${feature} management data:`, error);
          }
        }
      }

      setFormData(sectionsData);
      setManagementData(mgmtData);
      
    } catch (error) {
      console.error('Error loading organisation data:', error);
      setErrors({ general: 'Failed to load organisation data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setHasChanges(true);
    
    // Clear errors for this field
    if (errors[section]?.[field]) {
      setErrors(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: undefined
        }
      }));
    }
  };

  const handleManagementChange = (feature, field, value) => {
    setManagementData(prev => ({
      ...prev,
      [feature]: {
        ...prev[feature],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const validateSection = (sectionId) => {
    const sectionErrors = {};
    const sectionData = formData[sectionId] || {};

    // Basic validation rules
    switch (sectionId) {
      case 'about':
        if (!sectionData.description?.trim()) {
          sectionErrors.description = 'Description is required';
        }
        break;
      case 'contact':
        if (!sectionData.email?.trim()) {
          sectionErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sectionData.email)) {
          sectionErrors.email = 'Please enter a valid email address';
        }
        if (!sectionData.phone?.trim()) {
          sectionErrors.phone = 'Phone number is required';
        }
        break;
      case 'leadership':
        if (!sectionData.members?.length) {
          sectionErrors.members = 'At least one team member is required';
        }
        break;
    }

    return sectionErrors;
  };

  const handleSave = async (sectionId = null) => {
    try {
      setSaving(true);
      
      // Validate specific section or all sections
      const sectionsToValidate = sectionId ? [sectionId] : coreTabs.map(tab => tab.id);
      const allErrors = {};
      let hasValidationErrors = false;
      /*
      sectionsToValidate.forEach(section => {
        const sectionErrors = validateSection(section);
        if (Object.keys(sectionErrors).length > 0) {
          allErrors[section] = sectionErrors;
          hasValidationErrors = true;
        }
      });

      if (hasValidationErrors) {
        setErrors(allErrors);
        return;
      }
      */
      // Save content sections to pb-sections subcollection
      if (sectionId) {
        // Save specific section
        const sectionData = formData[sectionId];
        if (sectionData) {

          const orderValue = sectionData.order || getSectionOrder(sectionId);

          if (sectionData.id) {
            // Update existing section
            await organisationService.updateOrganisationSection(bookId, sectionData.id, sectionData);
          } else {
            // Add new section
            const newSectionId = await organisationService.addOrganisationSection(bookId, {
              ...sectionData,
              type: sectionId,
              order: orderValue
            });
            // Update local state with new ID
            setFormData(prev => ({
              ...prev,
              [sectionId]: { ...sectionData, id: newSectionId }
            }));
          }
        }
      } else {
        // Save all sections
        for (const [sectionType, sectionData] of Object.entries(formData)) {
          if (sectionData) {

            const orderValue = sectionData.order || getSectionOrder(sectionType);

            if (sectionData.id) {
              // Update existing section
              await organisationService.updateOrganisationSection(bookId, sectionData.id, sectionData);
            } else {
              // Add new section
              const newSectionId = await organisationService.addOrganisationSection(bookId, {
                ...sectionData,
                type: sectionType, 
                order: orderValue
              });
              // Update local state with new ID
              setFormData(prev => ({
                ...prev,
                [sectionType]: { ...sectionData, id: newSectionId }
              }));
            }
          }
        }

        // Save management data to pb-management subcollection
        for (const [feature, featureData] of Object.entries(managementData)) {
          if (featureData && Object.keys(featureData).length > 0) {
            await organisationService.updateManagementFeatureData(bookId, feature, featureData);
          }
        }
      }

      // Update the book record if needed
      if (onUpdate) {
        onUpdate({ 
          ...book, 
          organisationData: formData,
          managementData: managementData,
          updatedAt: new Date()
        });
      }

      setHasChanges(false);
      setErrors({});
      
      // Log activity
      await organisationService.logActivity(bookId, {
        entityType: 'organisation',
        action: sectionId ? 'section_updated' : 'data_saved',
        details: { section: sectionId || 'all_sections' },
        user: user?.displayName || user?.email || 'Unknown'
      });
      
    } catch (error) {
      console.error('Error saving organisation data:', error);
      setErrors({ general: 'Failed to save organisation data. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const getSectionOrder = (sectionType) => {
    const defaultOrders = {
      hero: 1,
      about: 2,
      leadership: 3,
      services: 4,
      events: 5,
      news: 6,
      contact: 7
    };
    return defaultOrders[sectionType] || 99;
  };

  const handleFileUpload = async (file, path) => {
    try {
      const result = await organisationService.uploadOrganisationMedia(file, bookId, activeTab, path);
      return result.url;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const currentTab = allTabs.find(tab => tab.id === activeTab);
  const SectionComponent = currentTab?.component;
  const isManagementTab = activeTab.startsWith('mgmt-');
  const managementFeature = isManagementTab ? activeTab.replace('mgmt-', '') : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading organisation data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Building className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {book?.botName || 'Organisation'} Manager
                </h1>
                <p className="text-gray-600">
                  Manage your organisation's content and information
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Save Button */}
              <button
                onClick={() => handleSave()}
                disabled={saving || !hasChanges}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Changes Indicator */}
          {hasChanges && (
            <div className="mt-3 flex items-center gap-2 text-sm text-amber-600">
              <AlertCircle className="h-4 w-4" />
              <span>You have unsaved changes. Don't forget to save!</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex">
            {/* Sidebar Navigation */}
            <div className="w-64 border-r border-gray-200 bg-gray-50">
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Content Sections</h3>
                <nav className="space-y-1">
                  {coreTabs.map((tab) => {
                    const Icon = tab.icon;
                    const hasErrors = errors[tab.id] && Object.keys(errors[tab.id]).length > 0;
                    const hasData = formData[tab.id] && Object.keys(formData[tab.id]).length > 0;
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="font-medium">{tab.label}</span>
                        <div className="ml-auto flex items-center gap-1">
                          {hasErrors && <AlertCircle className="h-3 w-3 text-red-500" />}
                          {hasData && !hasErrors && <CheckCircle className="h-3 w-3 text-green-500" />}
                        </div>
                      </button>
                    );
                  })}
                </nav>

                {/* Management Features */}
                {managementTabs.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Management</h3>
                    <nav className="space-y-1">
                      {managementTabs.map((tab) => {
                        const Icon = tab.icon;
                        
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors ${
                              activeTab === tab.id
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="font-medium">{tab.label}</span>
                          </button>
                        );
                      })}
                    </nav>
                  </div>
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 p-6">
              {errors.general && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Error</span>
                  </div>
                  <p className="text-red-700 mt-1">{errors.general}</p>
                </div>
              )}

              {SectionComponent && (
                <>
                  {isManagementTab ? (
                    <SectionComponent
                      data={managementData[managementFeature] || {}}
                      onChange={(field, value) => handleManagementChange(managementFeature, field, value)}
                      bookId={bookId}
                      onFileUpload={handleFileUpload}
                      errors={errors[managementFeature] || {}}
                    />
                  ) : (
                    <SectionComponent
                      data={formData[activeTab] || {}}
                      onChange={(field, value) => handleSectionChange(activeTab, field, value)}
                      templateType="organisation"
                      errors={errors[activeTab] || {}}
                      bookId={bookId}
                      onFileUpload={handleFileUpload}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};